import React from 'react';
import { View, Button, Icon, Heading, useViewport } from '@fairygodboss/ui';
import window from 'global/window';

import { GridColumnWrapper } from './GridColumnWrapper';
import { useEventRegistration } from '../EventRegistrationContext';
import { AuthWrapper } from '../../../../pages/HomePage_v2/sections/components/AuthWrapper';
import {
    EVENT_OVER_WITH_VIDEO_BANNER,
    getFormattedDateString,
    getFormattedTimeString,
    REGISTER_NOW_BUTTON,
    REGISTER_TO_JOIN,
} from './helpers';
import { AddToCalendarButton } from './AddToCalendarButton';
import { SocialShareCta } from './SocialShareCta';
import { SuccessSVG } from './SuccessSVG';
import { SuccessSVGXs } from './SuccessSVGXs';

const DescriptionItem = ({ iconVariant, text, ...others }) => (
    <View display="flex" alignItems="center" {...others}>
        <View height="20px">
            <Icon variant={iconVariant} />
        </View>
        <View ml="12px" as="span">
            {text}
        </View>
    </View>
);

const EventTitle = ({ title }) => (
    <Heading
        as="h1"
        data-qa="event-title"
        variant={{
            _: 'headingMd',
            sm: 'headingLg',
            md: 'heading2xl',
        }}
    >
        {title}
    </Heading>
);

const RegistrationFoldButton = ({
    onRegistrationButtonClick,
    registrationButtonText,
}) => (
    <Button
        data-qa="registration-button"
        onPress={onRegistrationButtonClick}
        size="large"
        width={{ _: '100%', sm: 'auto' }}
    >
        {registrationButtonText === REGISTER_NOW_BUTTON
            ? 'Register for event'
            : registrationButtonText}
    </Button>
);

RegistrationFoldButton.displayName = 'RegistrationFoldButton';

export const VideoPlayButton = ({ bottomView }) => {
    const { videoRef, setPlayVideo } = useEventRegistration();
    return (
        <Button
            onPress={() => {
                if (videoRef?.current) {
                    videoRef?.current?.scrollIntoView();
                    const scrolledY = window.scrollY;
                    if (scrolledY) {
                        window.scroll(0, scrolledY - 122);
                    }
                    setPlayVideo(true);
                }
            }}
            size="large"
            width={{ _: bottomView ? 'auto' : '100%', sm: 'auto' }}
        >
            Watch recording
        </Button>
    );
};

VideoPlayButton.displayName = 'VideoPlayButton';

EventTitle.displayName = 'EventTitle';

export const AboveTheFold = () => {
    const {
        event,
        attendeeCount,
        userRegistered,
        registrationButtonText,
        onRegistrationButtonClick,
        setRegistrationButtonText,
        eventEnded,
        bannerText,
        countdownObject,
        isInternalEvent,
        isInternalEventUpcoming,
    } = useEventRegistration();
    const { isXS } = useViewport();
    const {
        title,
        startTime,
        endTime,
        eventType,
        registrationDeactivated,
    } = event;
    const startDateString = getFormattedDateString(startTime);
    const startTimeString = getFormattedTimeString(startTime);
    const endTimeString = getFormattedTimeString(endTime);

    const hasBottomView =
        (userRegistered &&
            !registrationDeactivated &&
            !registrationButtonText?.length &&
            !eventEnded) ||
        registrationButtonText ||
        isInternalEventUpcoming ||
        bannerText === EVENT_OVER_WITH_VIDEO_BANNER;

    const { days, hours, minutes } = countdownObject;

    if (
        !isInternalEvent &&
        eventType === 'Webinar' &&
        !days &&
        !hours &&
        typeof minutes === 'number' &&
        !userRegistered
    ) {
        setRegistrationButtonText(REGISTER_TO_JOIN);
    }

    return (
        <GridColumnWrapper
            backgroundColor="#F6F2FE"
            gridAlignment="center"
            gridColumnSpacing={{ xs: 12, sm: 9, md: 12, lg: 10 }}
            pt={{ _: '32px', md: '40px' }}
            px={{ _: '16px', sm: '0px', md: '24px', lg: '0px' }}
            pb={{ _: '32px', md: '48px' }}
        >
            <View
                display="flex"
                css={{
                    button: {
                        backgroundColor: 'transparent',
                        paddingLeft: '0px',
                    },
                }}
            >
                <Button
                    leftIcon={<Icon variant="chevron_left_16" />}
                    variant="primaryLink"
                    size="large"
                    onPress={() => window.open('/events', '_self')}
                >
                    VIEW ALL EVENTS
                </Button>
            </View>
            <View pt="24px">
                <EventTitle title={title} />
            </View>
            <View
                display="flex"
                flexWrap="wrap"
                flexDirection={{ _: 'column', sm: 'row' }}
                pt={{ _: '18px', md: '26px', lg: '24px' }}
            >
                <DescriptionItem
                    mr={{ _: '0px', sm: '28px' }}
                    mb={{ _: '12px', md: '0px' }}
                    iconVariant="calendar_24"
                    text={startDateString}
                />
                <DescriptionItem
                    mr={{ _: '0px', sm: '28px' }}
                    mb={{ _: '12px', md: '0px' }}
                    iconVariant="clock_24"
                    text={`${startTimeString} - ${endTimeString} ET`}
                />
                <DescriptionItem
                    mb={{ _: '12px', md: '0px' }}
                    mr={{ _: '0px', sm: '28px' }}
                    iconVariant="camera_24"
                    text={eventType}
                />
                {attendeeCount ? (
                    <DescriptionItem
                        mb={{ _: '12px', md: '0px' }}
                        iconVariant="people_24"
                        text={`${attendeeCount} attending`}
                    />
                ) : null}
            </View>
            {hasBottomView ? (
                <View position="relative" mt={{ _: '14px', md: '22px' }}>
                    {userRegistered &&
                    !registrationDeactivated &&
                    !registrationButtonText?.length &&
                    !eventEnded ? (
                        <View display="flex" alignItems="center">
                            <AddToCalendarButton event={event} />
                            {isXS ? (
                                <>
                                    <SuccessSVG />
                                    <View
                                        as="span"
                                        fontWeight="600"
                                        css={{
                                            color: '#353535',
                                            marginLeft: '8px',
                                            fontWeight: '600',
                                        }}
                                    >
                                        You’re registered!
                                    </View>
                                </>
                            ) : (
                                <>
                                    <SuccessSVGXs />
                                    <View
                                        as="span"
                                        fontWeight="600"
                                        css={{
                                            color: '#353535',
                                            marginLeft: '8px',
                                            fontWeight: '600',
                                        }}
                                    >
                                        You’re In!
                                    </View>
                                </>
                            )}
                        </View>
                    ) : null}

                    {isInternalEventUpcoming ? (
                        <View display="flex" alignItems="center">
                            <AddToCalendarButton
                                event={event}
                                size="large"
                                width={{ _: '100%', sm: 'auto' }}
                            />
                        </View>
                    ) : null}

                    {registrationButtonText && !registrationDeactivated ? (
                        <View
                            display="flex"
                            css={{
                                '.wrap-auth-div': {
                                    width: '100%',
                                },
                            }}
                        >
                            <AuthWrapper
                                component={
                                    <RegistrationFoldButton
                                        onRegistrationButtonClick={
                                            onRegistrationButtonClick
                                        }
                                        registrationButtonText={
                                            registrationButtonText
                                        }
                                    />
                                }
                                redirectUrl={window?.location?.pathname}
                                loginTrigger="register-event"
                                hash="RegisterEvent"
                                cookie={{ name: 'REGISTER_EVENT', value: true }}
                            />
                        </View>
                    ) : null}
                    {bannerText === EVENT_OVER_WITH_VIDEO_BANNER ? (
                        <VideoPlayButton />
                    ) : null}
                    <View
                        top="0px"
                        right="0px"
                        position="absolute"
                        display={{ _: 'none', md: 'inherit' }}
                    >
                        <SocialShareCta
                            styles={{
                                position: {
                                    _: 'inherit',
                                    md: 'absolute',
                                },
                                right: { _: '0px', md: '-7px', lg: '-15px' },
                            }}
                        />
                    </View>
                </View>
            ) : null}
        </GridColumnWrapper>
    );
};

AboveTheFold.displayName = 'AboveTheFold';
